import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useCallback } from 'react'
import { useFocusEffect } from 'expo-router'
import { getItem, setItem } from '../lib/storage'
import { apiGet, apiPost } from '../lib/api'
import { appendChatMessage, updateChatMessageReactions } from '../lib/chatCache'
import { addBreadcrumb } from '../lib/reporting'
import { unreadCount, chatSeenKey, chatSessionSeenBoundary } from '../lib/chatHelpers'
import { advanceSeenMarker } from '../lib/chatSeen'
import type {
  ChatMessageResponse,
  ChatReactionEmoji,
  ChatReactionUpdateResponse,
} from '@warcarp/api-types'

const QK = (gameId: string) => ['games', gameId, 'chat']
// Shared with the game-list unread indicator — see lib/chatHelpers.chatSeenKey.
const SEEN_KEY = chatSeenKey

/**
 * @param enabled Gate the fetch on membership — chat is player-only, so a
 *   non-member viewing the lobby (State B) or a viewer mid-invite-redeem would
 *   otherwise 403 (and retry) against /chat. Defaults to true for the chat
 *   screen itself, which only mounts for members.
 */
export function useGameMessages(gameId: string, enabled = true) {
  return useQuery({
    queryKey: QK(gameId),
    queryFn: () => apiGet<ChatMessageResponse[]>(`/api/v1/games/${gameId}/chat`),
    enabled: !!gameId && enabled,
    // WS already pushes CHAT_MESSAGE via setQueryData so the cache is kept
    // fresh in real time. A staleTime of 0 caused spurious refetches on every
    // window focus / tab-switch (chat → game → chat). 60 s matches the longest
    // gap a slow typer would take between messages, giving the WS push time to
    // deliver before a background fetch fires.
    staleTime: 60_000,
    // Per-query window-focus refetches are redundant while the socket delivers
    // CHAT_MESSAGE events. The root AppLifecycleHandler still performs an
    // explicit active-query refresh after a meaningful background stay,
    // covering pushes missed while the socket was dead; the user can also
    // pull-to-refresh manually.
    refetchOnWindowFocus: false,
  })
}

export function usePostMessage(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ content }: { content: string }) =>
      apiPost<ChatMessageResponse>(`/api/v1/games/${gameId}/chat`, { content }),
    onSuccess: (msg) => {
      // Dedupe by id — the WS broadcast for this same message can land
      // before the POST response resolves (server publishes the event
      // before flushing the HTTP reply). Without this guard the sender's
      // screen would render the message twice while other devices saw
      // it once.
      qc.setQueryData<ChatMessageResponse[]>(QK(gameId), (old) =>
        appendChatMessage(old, msg),
      )
    },
  })
}

export function useToggleChatReaction(gameId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: string; emoji: ChatReactionEmoji }) =>
      apiPost<ChatReactionUpdateResponse>(
        `/api/v1/games/${gameId}/chat/${messageId}/reactions`,
        { emoji },
      ),
    onSuccess: ({ messageId, reactions }) => {
      qc.setQueryData<ChatMessageResponse[]>(QK(gameId), (old) =>
        updateChatMessageReactions(old, messageId, reactions),
      )
    },
  })
}

/**
 * Tracks unread chat messages per session using persisted storage.
 * - hasUnread — true when the latest message hasn't been seen
 * - sessionSeenId — last-seen ID captured for the current chat visit (drives
 *   the "NEW" divider)
 * - onChatOpen — call when navigating to chat screen
 * - markSeen — call while chat is active to update storage
 */
export function useChatUnread(gameId: string, messages: ChatMessageResponse[]) {
  const [persistedId, setPersistedId] = useState<string | null | undefined>(undefined)
  // The "NEW" divider boundary: initialized from storage on mount, then
  // recaptured whenever an in-game drawer opens. Frozen during each visit so
  // markSeen can update storage without moving the divider under the reader.
  const [sessionSeenId, setSessionSeenId] = useState<string | null | undefined>(undefined)
  const [captured, setCaptured] = useState(false)

  const loadPersistedId = useCallback(() => {
    void getItem(SEEN_KEY(gameId)).then((val) => {
      setPersistedId(val ?? null)
    })
  }, [gameId])

  useEffect(() => { loadPersistedId() }, [loadPersistedId])

  useFocusEffect(useCallback(() => { loadPersistedId() }, [loadPersistedId]))

  // Load the initial divider boundary straight from storage, before markSeen
  // can overwrite it. Reopening an in-game drawer explicitly captures the
  // current persisted marker through captureSessionBoundary below.
  useEffect(() => {
    let cancelled = false
    void getItem(SEEN_KEY(gameId)).then((val) => {
      if (cancelled) return
      setSessionSeenId(val ?? null)
      setCaptured(true)
    })
    return () => {
      cancelled = true
    }
  }, [gameId])

  const latestId = messages.at(-1)?.id ?? null
  const hasUnread = persistedId !== undefined && latestId !== null && persistedId !== latestId

  // Number of messages newer than the last-seen id. Drives the chat icon's
  // count badge (mirrors the cards HUD). Pure logic in lib/chatHelpers.
  const unread = unreadCount(messages, persistedId)

  const markSeen = useCallback(() => {
    // Gate on `captured` so we never persist the latest id (and lose the
    // divider boundary) before it has been read on mount.
    if (!captured || !latestId) return
    setItem(SEEN_KEY(gameId), latestId).catch(() => {
      // Persisting the last-seen chat message ID failed. This is non-fatal
      // (the "unread" dot resets on next session) but worth a breadcrumb so
      // repeated storage failures show up in Sentry context.
      addBreadcrumb({ category: 'chat', message: 'markSeen persist failed', level: 'warning' })
    })
    setPersistedId(latestId)
  }, [gameId, latestId, captured])

  // The log's on-screen chat lines (lib/chatSeen): advance the marker to the
  // newest one seen, never back. A line pushed out of view unseen stays unread.
  const markSeenThrough = useCallback((visibleIds: readonly string[]) => {
    if (!captured || persistedId === undefined) return
    const next = advanceSeenMarker(messages, persistedId, visibleIds)
    if (!next) return
    setItem(SEEN_KEY(gameId), next).catch(() => {
      addBreadcrumb({ category: 'chat', message: 'markSeenThrough persist failed', level: 'warning' })
    })
    setPersistedId(next)
  }, [captured, gameId, messages, persistedId])

  const captureSessionBoundary = useCallback(() => {
    setSessionSeenId((current) => chatSessionSeenBoundary(persistedId, current))
  }, [persistedId])

  // Opening the game chat drawer captures its boundary separately, then marks
  // the current tail seen.
  const onChatOpen = markSeen

  return {
    hasUnread,
    unreadCount: unread,
    sessionSeenId,
    persistedSeenId: persistedId,
    onChatOpen,
    markSeen,
    markSeenThrough,
    captureSessionBoundary,
  }
}
